import { isEmpty } from "lodash";

/**
 * Check if an objective can have children based on childrenMap
 * @param {String} objectiveId - The objective ID
 * @param {Object} childrenMap - Map of objective IDs to their children info
 * @returns {boolean|null} true if has children, false if no children, null if unknown
 */
export const canHaveChildren = (objectiveId, childrenMap) => {
  if (!childrenMap || !childrenMap[objectiveId]) {
    return null;
  }

  const childrenInfo = childrenMap[objectiveId];

  if (childrenInfo.children === undefined) {
    return null;
  }

  return childrenInfo.children && childrenInfo.children.length > 0;
};

/**
 * Check if node should show expand toggle
 * @param {String} objectiveId - The objective ID
 * @param {Object} childrenMap - Map of objective IDs to their children info
 * @returns {boolean} true if should show expand toggle
 */
export const shouldShowExpand = (objectiveId, childrenMap) => {
  const hasChildren = canHaveChildren(objectiveId, childrenMap);
  return hasChildren !== false;
};

/**
 * Create a tree node from an objective
 * @param {Object} objective - The objective object
 * @param {Object} childrenMap - Map of objective IDs to their children info
 * @param {Object} parentNode - The parent node (optional)
 * @returns {Object} Tree node
 */
export const createObjectiveTreeNode = (
  objective,
  childrenMap = {},
  parentNode = null
) => {
  const { id, name } = objective;
  const hasChildren = canHaveChildren(id, childrenMap);

  return {
    id,
    name,
    objective,
    active: false,
    toggled: false,
    isParentType: hasChildren !== false,
    loading: false,
    children: hasChildren === false ? [] : null,
    parentNode,
  };
};

/**
 * Build childrenMap from objectives array
 * @param {Array} objectives - Array of objective objects
 * @returns {Object} Map of objective ID to children info
 */
export const buildChildrenMap = (objectives) => {
  const map = {};

  if (!objectives || objectives.length === 0) {
    return map;
  }

  objectives.forEach((objective) => {
    map[objective.id] = {
      children: [],
      fetched: false,
    };
  });

  return map;
};

/**
 * Build root level tree from objectives
 * @param {Array} objectives - Array of objective objects
 * @param {Object} childrenMap - Map of objective IDs to their children info
 * @returns {Array} Tree data array
 */
export const buildObjectivesTree = (objectives, childrenMap = {}) => {
  if (isEmpty(objectives)) {
    return [];
  }

  return objectives.map((objective) =>
    createObjectiveTreeNode(objective, childrenMap)
  );
};

/**
 * Add child objectives to a parent node
 * @param {Object} parentNode - The parent tree node
 * @param {Array} childObjectives - Array of child objective objects
 * @param {Object} childrenMap - Map of objective IDs to their children info
 */
export const addChildObjectivesToNode = (
  parentNode,
  childObjectives,
  childrenMap = {}
) => {
  if (!parentNode) {
    return;
  }

  if (isEmpty(childObjectives)) {
    Object.assign(parentNode, {
      children: [
        {
          noActivation: true,
          isPlaceholder: true,
          name: "No child objectives",
          objective: {},
        },
      ],
      isParentType: false,
      loading: false,
    });
    return;
  }

  Object.assign(parentNode, {
    children: childObjectives.map((objective) =>
      createObjectiveTreeNode(objective, childrenMap, parentNode)
    ),
    isParentType: true,
    loading: false,
  });
};

/**
 * Find a node in the tree by ID
 * @param {Array|Object} tree - The tree data
 * @param {String} nodeId - The node ID to find
 * @returns {Object|null} The found node or null
 */
export const findNodeById = (tree, nodeId) => {
  if (!tree) {
    return null;
  }

  const searchArray = Array.isArray(tree) ? tree : [tree];

  for (let i = 0; i < searchArray.length; i += 1) {
    const node = searchArray[i];
    if (node.id === nodeId) {
      return node;
    }

    if (node.children && Array.isArray(node.children)) {
      const found = findNodeById(node.children, nodeId);
      if (found) {
        return found;
      }
    }
  }

  return null;
};

/**
 * Find all nodes in the tree with the same objective ID
 * @param {Array|Object} tree - The tree data
 * @param {String} nodeId - The node ID to find
 * @returns {Array} Array of all nodes with this ID
 */
export const findAllNodesById = (tree, nodeId) => {
  if (!tree) {
    return [];
  }

  const results = [];
  const searchArray = Array.isArray(tree) ? tree : [tree];

  const search = (nodes) => {
    nodes.forEach((node) => {
      if (node.id === nodeId) {
        results.push(node);
      }

      if (node.children && Array.isArray(node.children)) {
        search(node.children);
      }
    });
  };

  search(searchArray);
  return results;
};
